/**
 * Events in the channel sidebar: the server header's "N events" row (it
 * opens the list; with nothing scheduled it still offers "Events" to those
 * who may make one), the "Happening now" cards under it, and the strip a
 * voice or stage channel shows while an event is live in it (Discord's
 * "happening now" on the channel).
 */
import type { Snowflake } from '@neosocial/protocol';
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useT } from '../i18n';
import { useFlag } from '../store/config';
import { useData } from '../store/data';
import { officialGuildId, useActiveGuildId } from '../store/selectors';
import { Icon } from '../ui/kit';
import { mayCreateAny } from './perms';
import { eventsOf, liveEventIn, liveEventsOf, loadEvents, useEvents } from './store';
import { openEditor, openEvent, openEvents, whereOf } from './ui';
import type { MenuItem } from '../ui/overlay';

/** Under the server's banner: "3 events", and what is happening now. */
export function EventsHeaderRow() {
  const tr = useT();
  const { t } = tr;
  const on = useFlag('scheduled_events');
  const gid = useActiveGuildId();
  const n = useEvents((s) => eventsOf(s, gid).length);
  const live = useEvents(useShallow((s) => liveEventsOf(s, gid)));
  const canCreate = useData((d) => mayCreateAny(d, gid));
  const names = useData(useShallow((d) => live.map((e) => whereOf(d, tr, e).label)));
  // another server opened (Console v2): its events, once (READY loads the official one's)
  const loaded = useEvents((s) => !gid || !!s.loaded[gid]);
  const official = useData((d) => officialGuildId(d) === gid);
  useEffect(() => {
    if (on && gid && !loaded && !official) void loadEvents(gid);
  }, [on, gid, loaded, official]);
  if (!on || !gid || (!n && !canCreate)) return null;
  return (
    <div className="ev-side">
      <button
        type="button"
        className="ev-row"
        data-events={n}
        aria-label={`${n ? t('ev.count', { n }) : t('ev.none')}. ${t('ev.open')}`}
        onClick={openEvents}
      >
        <Icon name="calendar" />
        <span className="ev-row-t">{n ? t('ev.count', { n }) : t('ev.none')}</span>
        {live.length ? <i className="ev-dot" aria-hidden="true" /> : null}
        <Icon name="chev-right" cls="ev-row-chev" />
      </button>
      {live.map((e, i) => (
        <button
          key={e.id}
          type="button"
          className="ev-live"
          data-live-event={e.id}
          onClick={() => openEvent(e.id)}
        >
          <span className="ev-live-k">
            <i className="ev-dot" aria-hidden="true" />
            {t('ev.live')}
          </span>
          <span className="ev-live-n" dir="auto">
            {e.name}
          </span>
          <span className="ev-live-w">
            <bdi dir="auto">{names[i]}</bdi>
          </span>
        </button>
      ))}
    </div>
  );
}

/** Under a voice or stage channel's row while an event is live there. */
export function ChannelEventStrip({ channelId }: { channelId: Snowflake }) {
  const { t } = useT();
  const on = useFlag('scheduled_events');
  const e = useEvents((s) => liveEventIn(s, channelId));
  if (!on || !e) return null;
  return (
    <button
      type="button"
      className="ev-strip"
      data-channel-event={e.id}
      aria-label={t('ev.liveTip', { name: e.name })}
      data-tip={t('ev.liveTip', { name: e.name })}
      onClick={() => openEvent(e.id)}
    >
      <i className="ev-dot" aria-hidden="true" />
      <span className="ev-strip-k">{t('ev.live')}</span>
      <span className="ev-strip-n" dir="auto">
        {e.name}
      </span>
    </button>
  );
}

/** The server menu's "Events" and "Create event" (Discord's server dropdown). */
export function useEventMenuItems(): MenuItem[] {
  const { t } = useT();
  const on = useFlag('scheduled_events');
  const gid = useActiveGuildId();
  const canCreate = useData((d) => mayCreateAny(d, gid));
  if (!on || !gid) return [];
  return [
    { label: t('ev.title'), icon: 'calendar', run: openEvents },
    ...(canCreate
      ? [
          {
            label: t('ev.create'),
            icon: 'plus-circle' as const,
            run: () => openEditor(null, { back: null }),
          },
        ]
      : []),
  ];
}
